import { useCallback, type RefObject } from 'react'
import { useDayCutoff } from '../../../contexts/DayCutoffContext'
import { useTimeFormat } from '../../../hooks/useTimeFormat'
import { cn } from '../../../lib/utils'
import type { ScheduleMarker } from '../../../types'
import { useGridGeometry } from './useGridGeometry'
import { useLineDrag } from './useLineDrag'
import { useScheduleMarkersContext } from './scheduleMarkersContext'
import { ScheduleMarkerPin } from './ScheduleMarkerPin'
import { festivalMinutesToTop, minutesToClockLabel, timeToMinutes, type GridGeometry } from './timeUtils'

// Lines at the same minute: pins sit side by side so each stays tappable, and
// labels stack downward.
const PIN_STEP_PX = 22
const LABEL_STEP_PX = 14

/**
 * The selected day's schedule lines on the grid. Like the now-line, each line is
 * a full-grid layer; unlike it, the line and its label let taps through to the
 * sets underneath — only the pin at the left edge is interactive. The label
 * rides at the center of the screen, wherever the grid is scrolled. Both layers
 * (line 15, label + pin 18) sit under the now-line (20) and its handle (35), so
 * where they meet "now" stays on top; the pin is still tappable through the
 * now-line, which lets pointer events through.
 */
export function ScheduleMarkerLines({
  gridRef,
  gridMin,
  gridMax,
}: {
  gridRef: RefObject<HTMLDivElement | null>
  gridMin: number
  gridMax: number
}) {
  const { own, shared } = useScheduleMarkersContext()
  const cutoff = useDayCutoff()
  const geometry = useGridGeometry(gridRef)
  const all = [...own.map(m => ({ m, mine: true })), ...shared.map(m => ({ m, mine: false }))]
  const slots = new Map<number, number>()
  const placed = all.map(({ m, mine }) => {
    const minutes = timeToMinutes(m.time, cutoff)
    const slot = slots.get(minutes) ?? 0
    slots.set(minutes, slot + 1)
    return { m, mine, minutes, slot }
  })
  return (
    <>
      {placed.map(({ m, mine, minutes, slot }) => (
        <MarkerLine
          key={m.id}
          marker={m}
          mine={mine}
          minutes={minutes}
          slot={slot}
          gridRef={gridRef}
          gridMin={gridMin}
          gridMax={gridMax}
          geometry={geometry}
        />
      ))}
    </>
  )
}

function MarkerLine({
  marker,
  mine,
  minutes,
  slot,
  gridRef,
  gridMin,
  gridMax,
  geometry,
}: {
  marker: ScheduleMarker
  mine: boolean
  minutes: number
  slot: number
  gridRef: RefObject<HTMLDivElement | null>
  gridMin: number
  gridMax: number
  geometry: GridGeometry
}) {
  const { openEdit, move } = useScheduleMarkersContext()
  const { hourCycle, locale } = useTimeFormat()
  const onCommit = useCallback((min: number) => move(marker, min), [move, marker])
  const onTap = useCallback(() => openEdit(marker), [openEdit, marker])
  const { dragMin, startDrag } = useLineDrag({ gridRef, gridMin, gridMax, geometry, snap: 5, threshold: 4, onCommit, onTap })
  const dragging = dragMin !== null
  const top = festivalMinutesToTop(dragMin ?? minutes, gridMin, geometry.slotPx)

  const layer: React.CSSProperties = { gridColumn: '1 / -1', gridRow: '2 / -1', position: 'relative' }
  const row: React.CSSProperties = { position: 'absolute', top: `${top}px`, left: 0, right: 0, transform: 'translateY(-50%)' }

  return (
    <>
      <div className="pointer-events-none" style={{ ...layer, zIndex: 15 }}>
        <div className="flex items-center" style={row}>
          <div className="shrink-0 w-screen" style={{ position: 'sticky', left: 0 }}>
            <div
              className={cn(
                'ml-2 border-t',
                dragging ? 'border-solid border-foreground' : 'border-dashed border-foreground/60',
              )}
            />
          </div>
        </div>
      </div>
      {/* The viewer's own pins (their drag handles) sit over friends' avatars. */}
      <div className="pointer-events-none" style={{ ...layer, zIndex: mine ? 19 : 18 }}>
        <div className="flex items-center" style={row}>
          {/* A strip as wide as the scroller, pinned to its left edge: the pin sits
              at its start (the drag handle, like the now-line's) and the label at
              its center, so both stay put while the grid scrolls sideways. */}
          <div
            className="relative flex items-center justify-center shrink-0 w-[100cqw]"
            style={{ position: 'sticky', left: 0 }}
            data-testid="schedule-marker"
          >
            <div className="absolute flex items-center pl-1" style={{ left: slot * PIN_STEP_PX }}>
              <ScheduleMarkerPin marker={marker} mine={mine} dragging={dragging} onPointerDown={startDrag} />
            </div>
            <span
              // A shared line is a heads-up to the crew, so it reads as loud as your own.
              className="max-w-[55%] truncate rounded bg-background px-1.5 py-0.5 text-[10px] leading-none font-medium text-foreground"
              style={slot ? { transform: `translateY(${slot * LABEL_STEP_PX}px)` } : undefined}
            >
              <span className="tabular-nums">{minutesToClockLabel(dragMin ?? minutes, hourCycle, locale)}</span>
              {' · '}
              {marker.label}
            </span>
          </div>
        </div>
      </div>
    </>
  )
}
